"use client";

import React, { createContext, useContext } from "react";
import { AuthProvider } from "@/lib/auth/types";
import { getAuthProvider, normalizeAuthProvider } from "@/lib/auth/config";

/**
 * Carries the SERVER-resolved identity provider to client components.
 *
 * A browser cannot read AUTH_PROVIDER — only NEXT_PUBLIC_* survives into the
 * bundle, and only as a build-time constant. So the root layout, which is a
 * server component, resolves the value at request time and passes it in here.
 * That is what lets one image serve ecp-dev on Clerk and preprod/prod on
 * Keycloak.
 *
 * The value cannot change for the life of the process, which is what makes it
 * safe for lib/auth/index.ts to select hook implementations from it: the number
 * and order of hook calls stays fixed across every render.
 */
const AuthProviderNameContext = createContext<AuthProvider | null>(null);

export const AuthProviderNameProvider = ({
  provider,
  children,
}: React.PropsWithChildren<{ provider?: string }>) => (
  <AuthProviderNameContext.Provider value={normalizeAuthProvider(provider)}>
    {children}
  </AuthProviderNameContext.Provider>
);

/**
 * The active provider, as resolved by the server.
 *
 * Falls back to the build-time value when no provider has been threaded in, so
 * an app that has not adopted the prop keeps its previous behaviour rather than
 * silently becoming Clerk.
 */
export function useAuthProviderName(): AuthProvider {
  return useContext(AuthProviderNameContext) ?? getAuthProvider();
}
